import { useEffect, useState } from 'react';
import { m } from 'framer-motion';
import { ShieldCheck } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useInView } from '@/hooks/useInView';
import { CAPABILITIES_FALLBACK, getVerifiedStats } from '@/data/companyStats';

interface CounterProps {
  end: number;
  suffix: string;
  duration?: number;
  isInView: boolean;
}

const Counter = ({ end, suffix, duration = 2000, isInView }: CounterProps) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isInView) return;
    let start = 0;
    const step = end / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [isInView, end, duration]);

  return (
    <span className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-accent tabular-nums">
      {count}
      {suffix}
    </span>
  );
};

/**
 * Chiffres clés — source unique `src/data/companyStats.ts`.
 * Aucune statistique n'est affichée si `is_verified` est faux : le bloc
 * institutionnel « Nos capacités opérationnelles » le remplace alors.
 */
const KeyFiguresSection = () => {
  const { language } = useLanguage();
  const section = useInView(0.2);
  const fr = language === 'fr';
  const stats = getVerifiedStats();
  const fallback = CAPABILITIES_FALLBACK[fr ? 'fr' : 'en'];

  /** Sépare la partie numérique du suffixe (ex. "50+" → 50 / "+"). */
  const parseValue = (value: string) => {
    const digits = value.replace(/[^\d]/g, '');
    return {
      end: digits ? Number(digits) : 0,
      suffix: value.replace(/[\d\s\u202f]/g, ''),
      numeric: digits.length > 0,
    };
  };

  return (
    <section ref={section.ref} className="py-20 bg-primary relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-r from-mtb-blue-dark/50 to-transparent" />
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-accent/10 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative">
        {stats.length > 0 ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
            {stats.map((stat, i) => {
              const parsed = parseValue(stat.value as string);
              return (
                <m.div
                  key={stat.key}
                  className="text-center"
                  initial={{ opacity: 0, y: 30 }}
                  animate={section.isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: i * 0.15 }}
                >
                  <div className="mb-2">
                    {parsed.numeric ? (
                      <Counter
                        end={parsed.end}
                        suffix={parsed.suffix}
                        isInView={section.isInView}
                      />
                    ) : (
                      <span className="font-heading text-4xl md:text-5xl font-bold text-accent">
                        {stat.value}
                      </span>
                    )}
                  </div>
                  <p className="text-primary-foreground/70 text-sm font-medium">
                    {fr ? stat.label_fr : stat.label_en}
                  </p>
                </m.div>
              );
            })}
          </div>
        ) : (
          <m.div
            className="max-w-3xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={section.isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-accent/15 mb-5">
              <ShieldCheck className="w-7 h-7 text-accent" aria-hidden="true" />
            </div>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-primary-foreground mb-4">
              {fallback.title}
            </h2>
            <div className="w-16 h-1 bg-accent mx-auto mb-5" />
            <p className="text-primary-foreground/80 leading-relaxed text-base md:text-lg">
              {fallback.text}
            </p>
          </m.div>
        )}
      </div>
    </section>
  );
};

export default KeyFiguresSection;
